import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { SupportFoot } from '../core/supports/types';
import { SceneHost } from './scene';

/** Vite HMR: dispose the previous instance before remounting. */
let disposePrevious: (() => void) | null = null;

export type SupportFootSelection = {
  attachS: number;
  role: 'main' | 'triangle';
};

/**
 * Pickable ground pads + translate gizmo so a support base point can be
 * force-moved in the viewport (persisted as a forceFoot override).
 * Multi-select pads for an explicit mergeFeet override (two or more).
 */
export class SupportFootEditor {
  readonly group = new THREE.Group();
  private readonly handles = new THREE.Group();
  private readonly gizmo: TransformControls;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly pickMat = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0.01,
    depthWrite: false,
  });
  /** Unlit + depthTest off so multi-select stays visible without the translate gizmo. */
  private readonly selectMat = new THREE.MeshBasicMaterial({
    color: 0xe8a838,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    depthTest: false,
  });
  private readonly selectRingMat = new THREE.MeshBasicMaterial({
    color: 0xffd078,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
    depthTest: false,
  });
  private feet: SupportFoot[] = [];
  private selected: SupportFootSelection[] = [];
  private dragPivot = new THREE.Object3D();
  private enabled = true;

  onSelect: ((sels: SupportFootSelection[]) => void) | null = null;
  /** Fired while the gizmo moves a foot (world XZ; Y is terrain elsewhere). */
  onFootMove: ((sel: SupportFootSelection, x: number, z: number) => void) | null = null;
  onFootMoveStart: (() => void) | null = null;
  onFootMoveEnd: (() => void) | null = null;

  constructor(private host: SceneHost) {
    disposePrevious?.();

    this.group.name = 'supportFootEditor';
    this.handles.name = 'supportFootHandles';
    this.group.add(this.handles);
    this.group.add(this.dragPivot);

    this.gizmo = new TransformControls(host.camera, host.renderer.domElement);
    this.gizmo.setSize(0.85);
    this.gizmo.setMode('translate');
    // Keep pads on the ground plane — only plan (XZ) is authored.
    this.gizmo.showY = false;
    this.gizmo.enabled = false;
    host.scene.add(this.gizmo.getHelper());

    this.gizmo.addEventListener('dragging-changed', (e) => {
      const dragging = (e as { value?: boolean }).value === true;
      host.orbit.enabled = !dragging;
      if (dragging) this.onFootMoveStart?.();
      else this.onFootMoveEnd?.();
    });
    this.gizmo.addEventListener('objectChange', () => {
      const primary = this.selected[0];
      if (!primary || this.selected.length !== 1) return;
      this.onFootMove?.(primary, this.dragPivot.position.x, this.dragPivot.position.z);
    });

    disposePrevious = () => {
      this.gizmo.enabled = false;
      this.gizmo.detach();
      host.scene.remove(this.gizmo.getHelper());
      this.gizmo.dispose();
      this.clearHandles();
      this.pickMat.dispose();
      this.selectMat.dispose();
      this.selectRingMat.dispose();
    };
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.group.visible = on;
    if (!on) this.clearSelection();
  }

  /** Primary selected foot (gizmo target), or null. */
  get selection(): SupportFootSelection | null {
    return this.selected[0] ?? null;
  }

  get selections(): SupportFootSelection[] {
    return this.selected.slice();
  }

  isDragging(): boolean {
    return Boolean((this.gizmo as unknown as { dragging?: boolean }).dragging);
  }

  /** Rebuild invisible pick discs from the latest solver feet. */
  syncFeet(feet: SupportFoot[]): void {
    this.feet = feet.map((f) => ({
      attachS: f.attachS,
      role: f.role,
      pos: f.pos.clone(),
    }));
    const prev = this.selected;
    this.selected = this.selected.filter((sel) =>
      this.feet.some((f) => footKeyEq(f, sel)),
    );
    this.rebuildHandles();
    this.syncGizmo();
    if (!selectionsEqual(prev, this.selected)) {
      this.onSelect?.(this.selected.slice());
    }
  }

  clearSelection(): void {
    if (this.selected.length === 0) return;
    this.selected = [];
    this.detachGizmo();
    this.rebuildHandles();
    this.onSelect?.([]);
  }

  /** Replace selection with zero or one foot (plain click). */
  setSelection(sel: SupportFootSelection | null): void {
    if (!sel) {
      this.clearSelection();
      return;
    }
    this.setSelections([sel]);
  }

  /** Replace selection with any number of distinct feet. */
  setSelections(sels: SupportFootSelection[]): void {
    const next: SupportFootSelection[] = [];
    for (const sel of sels) {
      const foot = this.feet.find((f) => footKeyEq(f, sel));
      if (!foot) continue;
      if (next.some((s) => footKeyEq(s, foot))) continue;
      next.push({ attachS: foot.attachS, role: foot.role });
    }
    if (selectionsEqual(this.selected, next)) {
      this.syncGizmo();
      return;
    }
    this.selected = next;
    this.rebuildHandles();
    this.syncGizmo();
    this.onSelect?.(this.selected.slice());
  }

  /**
   * Ctrl/Shift multi-select: toggle `sel` in the set.
   */
  toggleInSelection(sel: SupportFootSelection): void {
    const foot = this.feet.find((f) => footKeyEq(f, sel));
    if (!foot) {
      this.clearSelection();
      return;
    }
    const key = { attachS: foot.attachS, role: foot.role };
    const idx = this.selected.findIndex((s) => footKeyEq(s, key));
    let next: SupportFootSelection[];
    if (idx >= 0) {
      next = this.selected.filter((_, i) => i !== idx);
    } else {
      next = [...this.selected, key];
    }
    this.selected = next;
    this.rebuildHandles();
    this.syncGizmo();
    this.onSelect?.(this.selected.slice());
  }

  hitsGizmo(camera: THREE.Camera, clientX: number, clientY: number, canvas: HTMLElement): boolean {
    if (!this.enabled || this.selected.length !== 1 || !this.gizmo.enabled) return false;
    this.setPointer(camera, clientX, clientY, canvas);
    const controls = this.gizmo as unknown as {
      _gizmo?: { picker?: Record<string, THREE.Object3D> };
      mode?: string;
    };
    const picker = controls._gizmo?.picker?.[controls.mode ?? 'translate'];
    const target = picker ?? this.gizmo.getHelper();
    return this.raycaster.intersectObject(target, true).length > 0;
  }

  /** Ray-pick a footer pad under the cursor. */
  pickAtScreen(
    camera: THREE.Camera,
    clientX: number,
    clientY: number,
    canvas: HTMLElement,
  ): SupportFootSelection | null {
    if (!this.enabled || !this.group.visible) return null;
    this.setPointer(camera, clientX, clientY, canvas);
    const hits = this.raycaster.intersectObjects(this.handles.children, false);
    for (const hit of hits) {
      const ud = hit.object.userData as { attachS?: number; role?: 'main' | 'triangle' };
      if (ud.attachS == null || ud.role == null) continue;
      return { attachS: ud.attachS, role: ud.role };
    }
    return null;
  }

  private setPointer(
    camera: THREE.Camera,
    clientX: number,
    clientY: number,
    canvas: HTMLElement,
  ): void {
    const rect = canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, camera);
  }

  private syncGizmo(): void {
    // Support regen runs on every foot move; never detach/reattach mid-drag or
    // TransformControls drops its arrows while the helper root stays visible.
    if (this.isDragging()) return;
    if (this.selected.length === 1) {
      const foot = this.feet.find((f) => footKeyEq(f, this.selected[0]));
      if (foot) {
        this.attachGizmoTo(foot.pos);
        return;
      }
    }
    this.detachGizmo();
  }

  private rebuildHandles(): void {
    this.clearHandles();
    for (const foot of this.feet) {
      const selected = this.selected.some((s) => footKeyEq(s, foot));
      if (selected) {
        // Fat pad + ring sit above the solid footer so multi-select stays obvious
        // even when the translate gizmo is hidden (2+ pads selected).
        const pad = new THREE.Mesh(
          new THREE.BoxGeometry(2.3, 0.22, 2.3),
          this.selectMat,
        );
        pad.position.set(foot.pos.x, foot.pos.y + 0.2, foot.pos.z);
        pad.userData = { attachS: foot.attachS, role: foot.role };
        pad.renderOrder = 6;
        this.handles.add(pad);

        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(1.35, 0.08, 8, 24),
          this.selectRingMat,
        );
        ring.rotation.x = Math.PI / 2;
        ring.position.set(foot.pos.x, foot.pos.y + 0.32, foot.pos.z);
        ring.userData = { attachS: foot.attachS, role: foot.role };
        ring.renderOrder = 7;
        this.handles.add(ring);
      } else {
        const geo = new THREE.BoxGeometry(1.7, 0.16, 1.7);
        const mesh = new THREE.Mesh(geo, this.pickMat);
        mesh.position.set(foot.pos.x, foot.pos.y + 0.06, foot.pos.z);
        mesh.userData = { attachS: foot.attachS, role: foot.role };
        mesh.renderOrder = 1;
        this.handles.add(mesh);
      }
    }
  }

  private clearHandles(): void {
    for (const child of [...this.handles.children]) {
      this.handles.remove(child);
      if (child instanceof THREE.Mesh) child.geometry.dispose();
    }
  }

  private attachGizmoTo(pos: THREE.Vector3): void {
    this.dragPivot.position.copy(pos);
    this.gizmo.attach(this.dragPivot);
    this.gizmo.enabled = true;
  }

  private detachGizmo(): void {
    this.gizmo.enabled = false;
    this.gizmo.detach();
  }
}

function footKeyEq(
  a: { attachS: number; role: 'main' | 'triangle' },
  b: { attachS: number; role: 'main' | 'triangle' },
): boolean {
  return a.role === b.role && Math.abs(a.attachS - b.attachS) < 1e-4;
}

function selectionsEqual(a: SupportFootSelection[], b: SupportFootSelection[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((s, i) => footKeyEq(s, b[i]));
}
